import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../auth/authConfig';

/**
 * Central Axios instance.
 *
 * Base URL resolution order:
 *  1. REACT_APP_API_URL env var (set in .env.local for dev, empty for prod)
 *  2. Empty string → relative paths (works with SWA proxy and APIM in production)
 */
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000, // 60s — agentic analysis can be slow
});

// ── Request interceptor: attach Bearer token from MSAL ──────────────────────
apiClient.interceptors.request.use(async (config) => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      config.headers.Authorization = `Bearer ${response.accessToken}`;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Token expired / consent required — redirect to login
        await msalInstance.acquireTokenRedirect(loginRequest);
      } else {
        console.error('[LISA] Token acquisition failed:', err);
      }
    }
  }
  return config;
});

// ── Response interceptor: normalise errors ────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    if (status === 401) {
      console.warn('[LISA] 401 Unauthorized — token may be invalid');
      // Don't auto-redirect to avoid loops; let the user re-login manually
    }

    const message =
      detail ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';

    return Promise.reject(new Error(message));
  }
);

export default apiClient;
